import { useEffect, useRef, useState } from 'react'

import type { GameSummary } from '../api/client'
import { useGameStore } from '../store/gameStore'
import { GamePreview } from './GamePreview'

/**
 * The demo lobby.
 *
 * A horizontal list: there will be more games than fit on screen, and a
 * vertical grid would require scrolling the whole page and would break
 * the "shelf" feeling. The contents of the list come from the server
 * (GET /games), so a new game shows up here after release, with no
 * client changes.
 */

const MECHANIC_LABEL: Record<string, string> = {
  ways: 'ways',
  tumble: 'cascades',
}

function describe(game: GameSummary): string {
  const grid = `${game.layout.reels}×${game.layout.rows}`
  const ways = game.mechanic === 'ways' ? `, ${game.layout.rows ** game.layout.reels} ways` : ''
  return `${grid}${ways}`
}

function GameCard({
  game,
  onOpen,
}: {
  game: GameSummary
  onOpen: (gameId: string) => void
}): React.JSX.Element {
  return (
    <button className="card" onClick={() => onOpen(game.gameId)} type="button">
      <div className="card__art">
        <GamePreview game={game} />
        <span className="card__mechanic">{MECHANIC_LABEL[game.mechanic] ?? game.mechanic}</span>
      </div>

      <div className="card__body">
        <span className="card__name">{game.name}</span>
        <span className="card__meta">
          {describe(game)} · up to ×{game.maxWinMultiplier.toLocaleString('en-US')}
        </span>

        <div className="card__tags">
          {game.modes.includes('ante') && <span className="tag">Ante</span>}
          {game.modes.includes('buy') && <span className="tag tag--buy">Buy FS</span>}
          <span className="tag tag--dim">v{game.mathVersion}</span>
        </div>
      </div>

      <span className="card__play">Play</span>
    </button>
  )
}

export function Lobby({ onOpen }: { onOpen: (gameId: string) => void }): React.JSX.Element {
  const games = useGameStore((s) => s.games)
  const railRef = useRef<HTMLDivElement>(null)
  const [overflows, setOverflows] = useState(false)

  // The arrows are only needed when the shelf really does overflow: with
  // two games they would hang at the edges of empty space.
  useEffect(() => {
    const rail = railRef.current
    if (!rail) return
    const measure = (): void => setOverflows(rail.scrollWidth > rail.clientWidth + 1)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(rail)
    return () => observer.disconnect()
  }, [games])

  const scroll = (dir: 1 | -1): void => {
    const rail = railRef.current
    if (!rail) return
    // The step is the card width: scrolling "one at a time" reads better
    // than a fixed pixel amount across different screen sizes.
    const card = rail.querySelector('.card')
    const step = card instanceof HTMLElement ? card.offsetWidth + 18 : rail.clientWidth * 0.8
    rail.scrollBy({ left: dir * step, behavior: 'smooth' })
  }

  return (
    <div className="lobby">
      <header className="header">
        <h1 className="title">Demo games</h1>
        <span className="subtitle">
          {games.length} {games.length === 1 ? 'game' : 'games'} on the server · pick one to enter the demo
        </span>
      </header>

      <div className="rail-wrap">
        {overflows && (
          <button
            className="rail-nav rail-nav--prev"
            onClick={() => scroll(-1)}
            aria-label="Previous games"
            type="button"
          >
            ‹
          </button>
        )}

        <div className={`rail ${overflows ? '' : 'rail--centered'}`} ref={railRef}>
          {games.map((game) => (
            <GameCard key={game.gameId} game={game} onOpen={onOpen} />
          ))}
        </div>

        {overflows && (
          <button
            className="rail-nav rail-nav--next"
            onClick={() => scroll(1)}
            aria-label="Next games"
            type="button"
          >
            ›
          </button>
        )}
      </div>
    </div>
  )
}
